import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * LazyMount — defers rendering its children until the placeholder enters
 * (or nears) the viewport. Keeps below-the-fold sections (categories,
 * news, secondary poster rows) out of the initial render path so LCP/TBT
 * stay small.
 */
export function LazyMount({
  children,
  rootMargin = "300px 0px",
  minHeight = 200,
  fallback = null,
}: {
  children: ReactNode;
  rootMargin?: string;
  minHeight?: number;
  fallback?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, rootMargin]);
  return (
    <div ref={ref} style={visible ? undefined : { minHeight }}>
      {visible ? children : fallback}
    </div>
  );
}
